import React, { useContext, useEffect, useState } from 'react'
import API from '../../../services/API'
import {
  Alert,
  AlertTitle,
  Autocomplete,
  Box,
  Button,
  Checkbox,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  Grid,
  InputAdornment,
  MenuItem,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import AnDateTimePicker from '../../../components/DateTime/DateTimePicker'
import { TenantSettingsContext } from '../../../App'
import moment from 'moment'
import EmployeeSelector from '../../events/EmployeeSelector'
import SmallModal from '../../../components/Modals/SmallModal'
import ShiftBonusEditor from '../../../components/Forms/ShiftBonusEditor'
import ShiftTravelEditor from '../../../components/Forms/ShiftTravelEditor'
import { getTravelPay, toTravelDraft } from '../../../helpers/shiftTravel'
import { PTSDutiesContext } from '.'

// Shifts added while building an unsaved duty only exist in the form until the
// duty itself is saved, so they carry a generated id that is not an ObjectId.
const isObjectId = (value) => /^[a-f\d]{24}$/i.test(String(value || ''))

const createStagedShiftId = () =>
  `staged-${moment().format('YYYYMMDDHHmmssSSS')}-${Math.random()
    .toString(36)
    .slice(2, 8)}`

const getDisplayEmployee = (selectedAssignee, assignedTo) => {
  if (!selectedAssignee || selectedAssignee.isSubcontractor) return null

  if (selectedAssignee.employee) {
    return {
      ...selectedAssignee.employee,
      userAccount: selectedAssignee.employee.userAccount || {
        _id: selectedAssignee._id,
        firstName: selectedAssignee.firstName,
        lastName: selectedAssignee.lastName,
        photograph: selectedAssignee.photograph,
      },
    }
  }

  return {
    _id: assignedTo,
    clinicalRole: selectedAssignee.clinicalRole,
    userAccount: {
      _id: selectedAssignee._id,
      firstName: selectedAssignee.firstName,
      lastName: selectedAssignee.lastName,
      photograph: selectedAssignee.photograph,
    },
  }
}

const getDisplaySubcontractor = (selectedAssignee) => {
  if (!selectedAssignee?.isSubcontractor) return null

  return {
    ...selectedAssignee.subcontractor,
    userAccount: selectedAssignee.subcontractor?.userAccount || {
      _id: selectedAssignee._id,
      firstName: selectedAssignee.firstName,
      lastName: selectedAssignee.lastName,
      photograph: selectedAssignee.photograph,
    },
  }
}

const getAssignedEmployeeId = (selectedAssignee) => {
  if (!selectedAssignee || selectedAssignee.isSubcontractor) return null
  return (
    selectedAssignee.employee?._id ||
    (typeof selectedAssignee.employee === 'string'
      ? selectedAssignee.employee
      : null) ||
    selectedAssignee.userAccount?.employee?._id ||
    selectedAssignee.userAccount?.employee ||
    selectedAssignee.employeeId ||
    null
  )
}

const getShiftSaveErrorMessage = (error) => {
  const data = error?.response?.data || error?.data || {}
  if (Array.isArray(data.issues) && data.issues.length) {
    return data.issues.map((issue) => issue.message).join(' ')
  }
  return data.error || data.message || 'Unable to save this shift.'
}

const AddShiftModal = ({
  open,
  onClose,
  onCreate,
  customSubmit,
  duty,
  setDuty,
  existingShift,
}) => {
  const { getUncoveredShifts, getUnassignedShifts } =
    useContext(PTSDutiesContext)
  const [addShiftForm, setAddShiftForm] = useState({})
  const [openConfirmDelete, setOpenConfirmDelete] = useState(null)
  const { orgConfig } = useContext(TenantSettingsContext)
  const [isEditMode, setIsEditMode] = useState(false)
  const [saving, setSaving] = useState(false)
  const [saveError, setSaveError] = useState('')

  useEffect(() => {
    if (!open) return
    setIsEditMode(!!existingShift)
    setSaveError('')
    setSaving(false)
  }, [open, existingShift])

  useEffect(() => {
    if (!open) return

    if (existingShift) {
      setAddShiftForm({
        assignedTo:
          existingShift.assignedTo && typeof existingShift.assignedTo === 'object'
          ? {
              firstName: existingShift.assignedTo.userAccount?.firstName,
              lastName: existingShift.assignedTo.userAccount?.lastName,
              photograph: existingShift.assignedTo.userAccount?.photograph,
              employee: existingShift.assignedTo,
              _id: existingShift.assignedTo.userAccount?._id,
            }
          : existingShift.assignedSubcontractor &&
              typeof existingShift.assignedSubcontractor === 'object'
          ? {
              firstName: existingShift.assignedSubcontractor.userAccount?.firstName,
              lastName: existingShift.assignedSubcontractor.userAccount?.lastName,
              photograph: existingShift.assignedSubcontractor.userAccount?.photograph,
              subcontractor: existingShift.assignedSubcontractor,
              _id: existingShift.assignedSubcontractor.userAccount?._id,
              isSubcontractor: true,
            }
          : null,
        assignedToSubcontractor: !!existingShift.assignedToSubcontractor,
        clinicalRole: existingShift.clinicalRole ?? null,
        start: existingShift.start ?? duty?.start ?? null,
        end: existingShift.end ?? duty?.end ?? null,
        payRate: existingShift.payRate ?? 0,
        bonuses: (existingShift.bonuses ?? []).map((bonus) => ({
          label: bonus.label ?? '',
          amount: bonus.amount ?? '',
        })),
        travel: toTravelDraft(existingShift.travel),
      })
    } else {
      setAddShiftForm({
        assignedTo: null,
        assignedToSubcontractor: false,
        clinicalRole: null,
        start: duty?.start ?? null,
        end: duty?.end ?? null,
        payRate: 0,
        bonuses: [],
        travel: null,
      })
    }
  }, [open, existingShift, duty])

  // useEffect(() => {
  //   if (duty) {
  //     setAddShiftForm((prev) => ({
  //       ...prev,
  //       start: duty.start,
  //       end: duty.end,
  //     }))
  //   }
  // }, [duty])

  // A staged shift has never reached the server, so editing it must stay local
  // rather than PUTting to /pts/shift with an id Mongo cannot cast.
  const isStagedShift = !!existingShift && !isObjectId(existingShift._id)
  const stageLocally =
    isStagedShift || (!existingShift && (customSubmit || !duty?._id))

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!addShiftForm?.start || !addShiftForm?.end) return
    setSaving(true)
    setSaveError('')

    const assignedTo = addShiftForm.assignedToSubcontractor
      ? null
      : getAssignedEmployeeId(addShiftForm.assignedTo)
    const assignedSubcontractor = addShiftForm.assignedToSubcontractor
      ? null
      : addShiftForm.assignedTo?.subcontractor?._id ?? null

    const payload = {
      ...addShiftForm,
      assignedTo,
      assignedSubcontractor,
      assignedToSubcontractor: !!addShiftForm.assignedToSubcontractor,
      start: moment(addShiftForm.start).toISOString(),
      end: moment(addShiftForm.end).toISOString(),
      staffCanBook: true,
      ...(duty && { ptsDuty: duty._id }),
    }
    const displayShift = {
      ...payload,
      assignedTo: getDisplayEmployee(addShiftForm.assignedTo, assignedTo),
      assignedSubcontractor: getDisplaySubcontractor(addShiftForm.assignedTo),
    }

    try {
      if (stageLocally) {
        if (assignedTo) {
          const issuesRes = await API.post('/pts/shift-assignment-issues', {
            assignedTo,
            start: payload.start,
            end: payload.end,
            clinicalRole: payload.clinicalRole,
          })
          if (issuesRes.status < 200 || issuesRes.status >= 300) {
            setSaveError(getShiftSaveErrorMessage({ response: issuesRes }))
            return
          }
          const blockingIssues = (issuesRes.data || []).filter(
            (issue) => issue.severity !== 'warning',
          )

          if (blockingIssues.length) {
            setSaveError(blockingIssues.map((issue) => issue.message).join(' '))
            return
          }
        }

        const stagedShift = {
          ...displayShift,
          _id: isStagedShift ? existingShift._id : createStagedShiftId(),
        }

        if (isStagedShift) {
          setDuty?.((prev) => ({
            ...prev,
            crewShifts: (prev.crewShifts ?? []).map((shift) =>
              shift._id === existingShift._id
                ? { ...shift, ...stagedShift }
                : shift,
            ),
          }))
        }

        customSubmit?.(stagedShift)
        onCreate?.(stagedShift)
        onClose()
        return
      }

      const res = existingShift
        ? await API.put(`/pts/shift/${existingShift._id}`, payload)
        : await API.post('/pts/shift', payload)

      if (res.status < 200 || res.status >= 300) {
        setSaveError(getShiftSaveErrorMessage({ response: res }))
        return
      }

      if (existingShift && setDuty) {
        const updatedShift = res.data ?? {
          ...existingShift,
          ...displayShift,
          start: payload.start,
          end: payload.end,
        }

        setDuty((prev) => ({
          ...prev,
          crewShifts: (prev.crewShifts ?? []).map((shift) =>
            shift._id === existingShift._id
              ? { ...shift, ...updatedShift }
              : shift,
          ),
        }))
      }

      onCreate?.({
        ...displayShift,
        ...(res.data ?? {}),
        assignedTo:
          res.data?.assignedTo && typeof res.data.assignedTo === 'object'
            ? res.data.assignedTo
            : displayShift.assignedTo,
        assignedSubcontractor:
          res.data?.assignedSubcontractor &&
          typeof res.data.assignedSubcontractor === 'object'
            ? res.data.assignedSubcontractor
            : displayShift.assignedSubcontractor,
      })
      getUncoveredShifts?.()
      getUnassignedShifts?.()
      onClose()
    } catch (err) {
      setSaveError(getShiftSaveErrorMessage(err))
    } finally {
      setSaving(false)
    }
  }

  const handleDeleteShift = async () => {
    if (!openConfirmDelete) return

    if (!isObjectId(openConfirmDelete._id)) {
      setDuty?.((prev) => ({
        ...prev,
        crewShifts: (prev.crewShifts ?? []).filter(
          (shift) => shift._id !== openConfirmDelete._id,
        ),
      }))
      setOpenConfirmDelete(null)
      onClose()
      return
    }

    await API.delete('/pts/shift/' + openConfirmDelete._id).then((res) => {
      if (res.status === 200) {
        setOpenConfirmDelete(null)
        onClose()
        getUncoveredShifts?.()
        getUnassignedShifts?.()
      }
    })
  }

  const displayHours = (() => {
    if (!addShiftForm?.start || !addShiftForm?.end) return 0

    const hours = moment(addShiftForm.end).diff(
      moment(addShiftForm.start),
      'hours',
      true,
    )

    if (isNaN(hours)) return 0

    return Number.isInteger(hours) ? hours : parseFloat(hours.toFixed(2))
  })()

  const bonusTotal = (addShiftForm?.bonuses ?? []).reduce(
    (sum, bonus) => sum + (Number(bonus?.amount) || 0),
    0,
  )
  const travelTotal = getTravelPay(addShiftForm?.travel)
  const payRate =
    displayHours * (addShiftForm?.payRate ?? 0) + bonusTotal + travelTotal
  const displayPayRate = (() => {
    if ((!addShiftForm?.payRate || !displayHours) && !bonusTotal && !travelTotal)
      return 0
    if (isNaN(payRate)) return 0
    return Number.isInteger(payRate) ? payRate : parseFloat(payRate.toFixed(2))
  })()

  const shiftAlreadyStarted = existingShift?.clock?.startedAt
  const shiftAlreadyEnded = existingShift?.clock?.endedAt
  const shiftAlreadyVerified = existingShift?.isVerified
  const shiftAlreadyPaid = existingShift?.isPaid
  const cannotBeEdited =
    shiftAlreadyStarted ||
    shiftAlreadyEnded ||
    shiftAlreadyVerified ||
    shiftAlreadyPaid
  // TODO: Allow editing of shifts

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      component="form"
      onSubmit={handleSubmit}
    >
      <DialogTitle>{isEditMode ? 'Edit' : 'Create'} Crew Shift</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <EmployeeSelector
            value={addShiftForm.assignedTo}
            onChange={(e, newValue) => {
              setSaveError('')
              setAddShiftForm((prev) => ({
                ...prev,
                assignedTo: newValue,
                assignedToSubcontractor: false,
                clinicalRole:
                  newValue?.subcontractor?.clinicalRole ||
                  newValue?.employee?.clinicalRole ||
                  newValue?.clinicalRole ||
                  prev.clinicalRole,
              }))
            }}
            fullWidth
            label="Assigned staff"
            includeSubcontractor
            disabled={!!addShiftForm.assignedToSubcontractor || cannotBeEdited}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={!!addShiftForm.assignedToSubcontractor}
                onChange={(e) => {
                  setSaveError('')
                  setAddShiftForm((prev) => ({
                    ...prev,
                    assignedToSubcontractor: e.target.checked,
                    assignedTo: e.target.checked ? null : prev.assignedTo,
                  }))
                }}
                disabled={cannotBeEdited}
              />
            }
            label="Covered by subcontractor without a user account"
          />
          <Autocomplete
            value={addShiftForm.clinicalRole}
            onChange={(e, newValue) => {
              setSaveError('')
              setAddShiftForm((prev) => ({
                ...prev,
                clinicalRole: newValue,
                payRate:
                  orgConfig?.orgSettings?.defaultPayRates?.[newValue] ??
                  prev.payRate,
              }))
            }}
            renderInput={(params) => (
              <TextField {...params} label="Clinical Role" required={true} />
            )}
            options={orgConfig?.orgSettings?.roles ?? []}
          />
          <Box>
            <Grid container spacing={2}>
              <Grid item xs={12} lg={5}>
                <AnDateTimePicker
                  label="Start Time"
                  date={addShiftForm?.start ?? null}
                  onChange={(val) => {
                    setSaveError('')
                    setAddShiftForm({ ...addShiftForm, start: val })
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                  required
                  error={moment(addShiftForm.start).isSameOrAfter(
                    addShiftForm.end,
                  )}
                />
              </Grid>
              <Grid item xs={12} lg={5}>
                <AnDateTimePicker
                  label="End Time"
                  date={addShiftForm?.end ?? null}
                  onChange={(val) => {
                    setSaveError('')
                    setAddShiftForm({ ...addShiftForm, end: val })
                  }}
                  slotProps={{ textField: { fullWidth: true } }}
                  required
                  error={moment(addShiftForm.end).isSameOrBefore(
                    addShiftForm.start,
                  )}
                />
              </Grid>
              <Grid item xs={0} lg={2}>
                <Stack alignItems="center">
                  <Typography
                    variant="title2"
                    color={displayHours < 0 && 'error'}
                  >
                    {displayHours}
                  </Typography>
                  <Typography>hours</Typography>
                </Stack>
              </Grid>
              <Grid item xs={10}>
                <TextField
                  value={addShiftForm?.payRate ?? 0}
                  onChange={(e) => {
                    setSaveError('')
                    setAddShiftForm((prev) => ({
                      ...prev,
                      payRate: e.target.value,
                    }))
                  }}
                  type="number"
                  label="Hourly Rate"
                  fullWidth
                  sx={{ flex: 2 }}
                  autoComplete="off"
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">£</InputAdornment>
                    ),
                  }}
                />
              </Grid>
              <Grid item xs={2}>
                <Stack width={80} alignItems="center">
                  <Typography variant="title2">
                    {orgConfig.currencyIcon ?? '£'}
                    {displayPayRate}
                  </Typography>
                  <Typography>total</Typography>
                </Stack>
              </Grid>
              <Grid item xs={12}>
                <ShiftBonusEditor
                  bonuses={addShiftForm?.bonuses ?? []}
                  onChange={(bonuses) => {
                    setSaveError('')
                    setAddShiftForm((prev) => ({ ...prev, bonuses }))
                  }}
                  disabled={cannotBeEdited}
                />
              </Grid>
              <Grid item xs={12}>
                <ShiftTravelEditor
                  travel={addShiftForm?.travel ?? null}
                  onChange={(travel) => {
                    setSaveError('')
                    setAddShiftForm((prev) => ({ ...prev, travel }))
                  }}
                  disabled={cannotBeEdited}
                  currency={orgConfig?.orgSettings?.currency ?? '£'}
                  defaultRate={orgConfig?.orgSettings?.defaultTravelRate}
                />
              </Grid>
              <Grid item xs={12}>
                <Stack>
                  {saveError && <Alert severity="error">{saveError}</Alert>}
                  {shiftAlreadyPaid ? (
                    <Alert severity="error">
                      This shift has already been marked as paid and cannot be
                      edited.
                    </Alert>
                  ) : shiftAlreadyVerified ? (
                    <Alert severity="info">
                      This shift has already been marked as verified and cannot
                      be edited.
                    </Alert>
                  ) : shiftAlreadyEnded ? (
                    <Alert severity="info">
                      The staff member has already clocked out of this shift and
                      it cannot be edited.
                    </Alert>
                  ) : shiftAlreadyStarted ? (
                    <Alert severity="info">
                      The employee has already clocked in for this shift and it
                      cannot be edited.
                    </Alert>
                  ) : (
                    ''
                  )}
                </Stack>
              </Grid>
            </Grid>
          </Box>
        </Stack>
      </DialogContent>

      <DialogActions>
        <Stack
          direction="row"
          width="100%"
          justifyContent={isEditMode ? 'space-between' : 'flex-end'}
        >
          {isEditMode && !cannotBeEdited && (
            <Button
              onClick={() => setOpenConfirmDelete(existingShift)}
              variant="outlined"
              color="anRed"
            >
              Delete
            </Button>
          )}
          <Stack direction="row" gap={1}>
            <Button onClick={onClose}>Cancel</Button>
            {!cannotBeEdited && (
              <Button
                variant="contained"
                type="submit"
                disabled={saving || displayHours < 0}
              >
                {saving
                  ? 'Saving...'
                  : `${isEditMode ? 'Update' : 'Create'} Shift`}
              </Button>
            )}
          </Stack>
        </Stack>
      </DialogActions>
      <SmallModal
        open={!!openConfirmDelete}
        onClose={() => setOpenConfirmDelete(null)}
      >
        <Stack gap={1}>
          <Typography textAlign="center">
            Are you sure you want to delete this shift?
          </Typography>
          <Button
            size="small"
            fullWidth
            color="anRed"
            variant="outlined"
            onClick={() => handleDeleteShift()}
          >
            Yes - Delete
          </Button>
          <Button
            size="small"
            fullWidth
            color="anBlue"
            variant="contained"
            onClick={() => setOpenConfirmDelete(null)}
          >
            No - Cancel
          </Button>
        </Stack>
      </SmallModal>
    </Dialog>
  )
}

export default AddShiftModal
